/**
 * The top bar's filters on touch rows: whose touches, and the columns the rows carry.
 * Shared by Touch detail and Touch drivers, so a drill from one to the other lands on the
 * same rows.
 */
import { useMemo } from 'react'
import { useAppData } from './AppData'
import { applyFilters, useFilters, type Filters } from './filters'
import { useLoadedDataset } from './DatasetProvider'
import { claimSplitOf } from './claimCells'
import { indexesOf } from './blankValue'
import type { TouchDetailData } from './touchDetail'
import type { Person } from './selectors'

export type RowPick = { col: readonly number[]; want: ReadonlySet<number> }

/**
 * Whose touches: the scope's people with the Facility group filter off — it selects the
 * touch rows themselves (`touchColumnPicks`), exactly, by their facility group, and
 * choosing people by their share of the groups' billed claims would drop someone who
 * touched a claim there without billing one. `clear` turns further filters off the
 * same way (Touch drivers judges a stuck claim on every department's touches).
 */
export function useTouchPeople(clear: Partial<Filters> = {}): Person[] {
  const { scope, derived, crosstab } = useAppData()
  const { filters } = useFilters()
  const dataset = useLoadedDataset()
  const split = useMemo(() => claimSplitOf(dataset), [dataset])
  const key = JSON.stringify(clear)
  return useMemo(
    () =>
      filters.facilityGroup.length === 0 && Object.keys(clear).length === 0
        ? scope.people
        : applyFilters(derived, dataset.composition, { ...filters, facilityGroup: [], ...clear }, split, crosstab).people,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filters, scope.people, derived, dataset.composition, split, crosstab, key],
  )
}

/**
 * The top bar's Overview slice (its facility group, facility, state and payer type) and
 * Facility group filter, matched on the rows' own columns: exact. The slice's department
 * is not a touch column; the slice's people stand in for it.
 */
export function touchColumnPicks(data: TouchDetailData | null, filters: Filters): RowPick[] {
  if (!data) return []
  const out: RowPick[] = []
  const sm = filters.slice?.match
  if (sm) {
    const by = (key: 'fg' | 'f' | 's' | 'p', v: string | null | undefined) => {
      if (v) out.push({ col: data.cols[key], want: new Set(indexesOf(data.dict[key], v)) })
    }
    by('fg', sm.facilityGroup)
    by('f', sm.facility)
    by('s', sm.state)
    by('p', sm.payer)
  }
  if (filters.facilityGroup.length > 0)
    out.push({
      col: data.cols.fg,
      want: new Set(filters.facilityGroup.flatMap((g) => indexesOf(data.dict.fg, g))),
    })
  return out
}
